import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Instagram, Download, Send, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactLinks = [
  {
    icon: Mail,
    label: "mijomijoarun@gmail.com",
    href: "mailto:mijomijoarun@gmail.com",
    testId: "contact-email-link",
  },
  {
    icon: Phone,
    label: "+91 7200114629",
    href: "tel:+917200114629",
    testId: "contact-phone-link",
  },
  {
    icon: Instagram,
    label: "Instagram / arun_mijo",
    href: "https://www.instagram.com/arun_mijo/",
    testId: "contact-instagram-link",
  },
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      console.error("Contact form error:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="section-number mb-4">07 &middot; Contact</p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none text-white max-w-3xl"
            data-testid="contact-heading"
          >
            Let's build{" "}
            <span className="font-serif-accent text-amber-400">
              the frame.
            </span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-neutral-400 leading-relaxed max-w-2xl">
            Available for film AI-VFX ideation, cinematic look development,
            sequence previz, set extension planning, character look books, and
            production-facing visual workflows.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16">
          {/* Links */}
          <div className="space-y-4">
            {contactLinks.map((link) => (
              <motion.a
                key={link.testId}
                href={link.href}
                target={link.href.startsWith("http") ? "_blank" : undefined}
                rel={
                  link.href.startsWith("http")
                    ? "noopener noreferrer"
                    : undefined
                }
                data-testid={link.testId}
                className="glass-surface glass-surface-hover rounded-2xl p-5 flex items-center gap-4 transition-all duration-500 group block"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <link.icon size={18} className="text-amber-500" />
                </div>
                <span className="text-sm text-neutral-300 group-hover:text-white transition-colors">
                  {link.label}
                </span>
                <span className="ml-auto text-neutral-600 group-hover:text-amber-500 transition-colors text-sm">
                  {"\u2197"}
                </span>
              </motion.a>
            ))}

            <motion.a
              href="#contact"
              data-testid="contact-resume-download"
              className="glass-surface glass-surface-hover rounded-2xl p-5 flex items-center gap-4 transition-all duration-500 group block"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Download size={18} className="text-amber-500" />
              </div>
              <span className="text-sm text-neutral-300 group-hover:text-white transition-colors">
                Download Resume PDF
              </span>
              <span className="ml-auto text-neutral-600 group-hover:text-amber-500 transition-colors text-sm">
                {"\u2193"}
              </span>
            </motion.a>
          </div>

          {/* Contact form */}
          <motion.form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="glass-surface rounded-2xl p-8 space-y-5"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <label className="text-xs font-mono tracking-[0.15em] uppercase text-neutral-500 block mb-2">
                Name
              </label>
              <input
                data-testid="contact-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-amber-500/50 transition-colors"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="text-xs font-mono tracking-[0.15em] uppercase text-neutral-500 block mb-2">
                Email
              </label>
              <input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-amber-500/50 transition-colors"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-xs font-mono tracking-[0.15em] uppercase text-neutral-500 block mb-2">
                Message
              </label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-amber-500/50 transition-colors resize-none"
                placeholder="Tell me about your project..."
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 text-black font-medium rounded-full hover:bg-amber-400 disabled:opacity-50 transition-all duration-300 text-sm"
            >
              {sent ? (
                <>
                  <CheckCircle size={16} />
                  Message Sent
                </>
              ) : sending ? (
                "Sending..."
              ) : (
                <>
                  <Send size={16} />
                  Send Message
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
